import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../api-config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Eye, EyeOff } from "lucide-react";

const UserDetail = () => {
  const navigate = useNavigate();
  const user_id = useParams().id;
  
  // Declaración del estado
  const [user, setUser] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone_number: ''
  });
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [newRole, setNewRole] = useState('');
  const [currentPassword, setCurrentPassword] = useState(''); // Estado para la contraseña actual
  const [newPassword, setNewPassword] = useState(''); // Estado para la nueva contraseña
  const [showCurrentPassword, setShowCurrentPassword] = useState(false); // Control de visibilidad para la contraseña actual
  const [showNewPassword, setShowNewPassword] = useState(false); // Control de visibilidad para la nueva contraseña
  const token = localStorage.getItem('token');

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/${user_id}/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setRoles(response.data);
    } catch (err) {
      toast.error('Failed to load roles');
    }
  };

  const fetchAllRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/roles/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAllRoles(response.data);
    } catch (err) {
      toast.error('Failed to load all roles');
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API_URL}/users/${user_id}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUser(response.data);
      } catch (err) {
        toast.error(err.response?.data.detail || 'Failed to fetch user');
      }
    };

    fetchUser();
    fetchAllRoles();
    fetchRoles();
  }, [user_id]);

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(
        `${API_URL}/user/${user_id}/`,
        {
          username: user.username,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          phone_number: user.phone_number
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUser(response.data);
      toast.success('User updated successfully!');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to update user';
      toast.error(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`${API_URL}/users/${user_id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('User deleted successfully!');
      navigate('/users');
    } catch (err) {
      toast.error(err.response?.data.detail || 'Failed to delete user');
    }
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API_URL}/users/add_roles`,
        { user_id : user_id
          ,role_id: newRole },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setNewRole('');
      fetchRoles();
      toast.success('Role assigned successfully!');
    } catch (err) {
      toast.error(err.response?.data.detail || 'Failed to assign role');
    }
  };

  const handleRemoveRole = async (role_id) => {
    try {
      await axios.delete(`${API_URL}/roles/${role_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchRoles();
      toast.success('Role removed successfully!');
    } catch (err) {
      toast.error(err.response?.data.detail || 'Failed to remove role');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/user/change-password`,
        {
          user_id,
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCurrentPassword('');
      setNewPassword('');
      toast.success('Password updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
  };

  return (
    <div className="flex flex-col md:flex-row justify-around bg-slate-900 min-h-screen p-12 text-white">
  {/* Información del Usuario */}

  <button
        onClick={() => navigate('/users')}
        className="fixed bottom-6 right-6 z-20 bg-purple-600 text-white px-4 py-2 rounded-full hover:bg-purple-700 transition-all duration-300 shadow-lg"
      >
        Volver
  </button>

  <div className="w-full md:w-1/2 max-w-lg p-8 space-y-6 bg-slate-800 rounded-lg shadow-lg border border-purple-400 mb-6 md:mb-0 md:mr-6">
    <h2 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-200">
      Información del Usuario
    </h2>
    <div>
      <label className="block text-sm font-medium text-white">Nombre de Usuario</label>
      <input
        type="text"
        value={user.username}
        onChange={(e) => setUser({ ...user, username: e.target.value })}
        className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-white">Email</label>
      <input
        type="email"
        value={user.email}
        readOnly
        className="w-full px-4 py-2 mt-1 text-gray-400 bg-slate-700 border border-purple-400 rounded-lg cursor-not-allowed"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-white">Nombre</label>
      <input
        type="text"
        value={user.first_name || ''}
        onChange={(e) => setUser({ ...user, first_name: e.target.value })}
        className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-white">Apellido</label>
      <input
        type="text"
        value={user.last_name || ''}
        onChange={(e) => setUser({ ...user, last_name: e.target.value })}
        className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-white">Teléfono</label>
      <input
        type="text"
        value={user.phone_number || ''}
        onChange={(e) => setUser({ ...user, phone_number: e.target.value })}
        className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
      />
    </div>
    <button
      onClick={handleUpdateUser}
      className="w-full px-4 py-2 mt-4 text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
    >
      Actualizar Usuario
    </button>
    <button
      onClick={handleDeleteUser}
      className="w-full px-4 py-2 mt-4 text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
    >
      Eliminar Usuario
    </button>
  </div>

  {/* Gestión de Roles */}
  <div className="w-full md:w-1/2 max-w-lg p-8 space-y-6 bg-slate-800 rounded-lg shadow-lg border border-purple-400 ml-4">
    <h2 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-200">
      Roles del Usuario
    </h2>
    <div>
      <label className="block text-sm font-medium text-white">Asignar Rol</label>
      <select
        value={newRole}
        onChange={(e) => setNewRole(e.target.value)}
        className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
      >
        <option value="">Selecciona un rol</option>
        {allRoles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.name}
          </option>
        ))}
      </select>
      <button
        onClick={handleAssignRole}
        className="w-full px-4 py-2 mt-4 text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
      >
        Asignar Rol
      </button>
    </div>
    <div className="mt-6">
      <h3 className="block text-sm font-medium text-white">Roles Asignados</h3>
      <ul>
        {roles.map((role) => (
          <li
            key={role.id}
            className="flex items-center justify-between p-2 px-6 bg-slate-700 rounded-lg border border-purple-400"
          >
            <span className="text-white">{role.name}</span>
            <button
              onClick={() => handleRemoveRole(role.id)}
              className="text-red-400 hover:text-red-500"
            >
              Eliminar
            </button>
          </li>
        ))}
      </ul>
    </div>
  </div>

  {/* Cambio de Contraseña */}
  <div className="w-full md:w-1/2 max-w-lg p-8 space-y-6 bg-slate-800 rounded-lg shadow-lg border border-purple-400 ml-4">
    <h2 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-200">
      Cambiar Contraseña
    </h2>
    <div>
      <label className="block text-sm font-medium text-white">Contraseña Actual</label>
      <div className="relative flex">
        <input
          type={showCurrentPassword ? "text" : "password"}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
        />
        <button
          type="button"
          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 px-2 rounded-lg text-white"
        >
          {showNewPassword ? <EyeOff className="h-5 w-5 mt-1" /> : <Eye className="h-5 w-5 mt-1" />}
        </button>
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-white">Nueva Contraseña</label>
      <div className="relative flex">
        <input
          type={showNewPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="w-full px-4 py-2 mt-1 text-white bg-slate-700 border border-purple-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-slate-600"
        />
        <button
          type="button"
          onClick={() => setShowNewPassword(!showNewPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5 px-2 rounded-lg text-white"
        >
          {showNewPassword ? <EyeOff className="h-5 w-5 mt-1" /> : <Eye className="h-5 w-5 mt-1" />}
        </button>
      </div>
    </div>
    <button
      onClick={handleChangePassword}
      className="w-full px-4 py-2 mt-4 text-white bg-purple-600 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
    >
      Cambiar Contraseña
    </button>
  </div>
</div>
  );
};

export default UserDetail;
